import React, { createContext, useContext, useState, useCallback, useRef } from 'react';
import Toast from './components/ui/Toast';

const ToastContext = createContext(null);

export function ToastProvider({ children, maxToasts = 5 }) {
  const [toasts, setToasts] = useState([]);
  const idCounter = useRef(0);

  const hideToast = useCallback((id) => {
    setToasts(prev => prev.filter(t => t.id !== id));
  }, []);

  const showToast = useCallback((...args) => {
    const id = ++idCounter.current;
    let config;

    if (typeof args[0] === 'object' && args[0] !== null && !Array.isArray(args[0]) && args[0].message) {
      config = { ...args[0], id };
    } else {
      config = {
        id,
        message: args[0],
        type: args[1] || 'success',
        duration: args[2] || 3200,
      };
    }

    setToasts(prev => {
      const next = [...prev, config];
      return next.length > maxToasts ? next.slice(next.length - maxToasts) : next;
    });

    return id;
  }, [maxToasts]);

  const toastApi = useCallback((message, opts) => {
    showToast(message, opts);
  }, [showToast]);

  toastApi.success = useCallback((message, opts) => {
    showToast(typeof message === 'object' ? { ...message, type: 'success' } : { message, type: 'success', ...opts });
  }, [showToast]);

  toastApi.error = useCallback((message, opts) => {
    showToast(typeof message === 'object' ? { ...message, type: 'error' } : { message, type: 'error', ...opts });
  }, [showToast]);

  toastApi.warning = useCallback((message, opts) => {
    showToast(typeof message === 'object' ? { ...message, type: 'warning' } : { message, type: 'warning', ...opts });
  }, [showToast]);

  toastApi.info = useCallback((message, opts) => {
    showToast(typeof message === 'object' ? { ...message, type: 'info' } : { message, type: 'info', ...opts });
  }, [showToast]);

  toastApi.dismiss = useCallback((id) => {
    hideToast(id);
  }, [hideToast]);

  toastApi.dismissAll = useCallback(() => {
    setToasts([]);
  }, []);

  return (
    <ToastContext.Provider value={toastApi}>
      {children}
      {toasts.map((t, idx) => (
        <Toast
          key={t.id}
          message={t.message}
          title={t.title}
          description={t.description}
          type={t.type}
          duration={t.duration}
          position={t.position}
          action={t.action}
          closeAfterAction={t.closeAfterAction}
          icon={t.icon}
          showProgress={t.showProgress}
          progressColor={t.progressColor}
          animation={t.animation}
          pauseOnHover={t.pauseOnHover}
          radius={t.radius}
          size={t.size}
          transitionDuration={t.transitionDuration}
          stack={t.stack}
          index={idx}
          stackSpacing={t.stackSpacing}
          showCloseButton={t.showCloseButton}
          autoDismiss={t.autoDismiss}
          onDismiss={t.onDismiss}
          persistent={t.persistent}
          priority={t.priority}
          closeIcon={t.closeIcon}
          progressPosition={t.progressPosition}
          keyboardDismiss={t.keyboardDismiss}
          autoFocus={t.autoFocus}
          rtl={t.rtl}
          sound={t.sound}
          customVariant={t.customVariant}
          onClose={() => hideToast(t.id)}
        />
      ))}
    </ToastContext.Provider>
  );
}

export function useToast() {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error('useToast must be used within ToastProvider');
  return ctx;
}
